import type {
  ContractsPerWorkedDayDefinitionInput,
  ContractsValueObjectsAmountStrategyTypeEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { InMemoryAdditionalCompensation } from 'modules/contracts/lib/contractFlowContext'

import { PER_WORKED_DAY_STRATEGY } from 'modules/contracts/lib/additionalCompensationStrategies'
import { manageUpperLimit } from 'modules/contracts/lib/amountStrategyUtils'

type ContractsPerWorkedDayDefinition = NonNullable<
  InMemoryAdditionalCompensation['perWorkedDayDefinition']
>

type PerWorkedDayMappableAdditionalCompensation = Pick<
  InMemoryAdditionalCompensation,
  'amountStrategyType' | 'perWorkedDayDefinition'
>

type NewAdditionalCompensationInput = {
  id?: string
  amount?: number | null
  amountStrategyType: ContractsValueObjectsAmountStrategyTypeEnum
  contractVersionId: string
  description?: string | null
  firstPaymentOn?: string | null
  payrollConceptId: string
  perWorkedDayDefinition?: ContractsPerWorkedDayDefinitionInput | null
  recurrence?: InMemoryAdditionalCompensation['recurrence']
  upperLimit?: number | null
}

const getAmountStrategyType = (
  amountStrategyType: InMemoryAdditionalCompensation['amountStrategyType']
): ContractsValueObjectsAmountStrategyTypeEnum => {
  if (!amountStrategyType) {
    throw new Error('Additional compensation amount strategy type is required')
  }

  return amountStrategyType
}

const getRequiredString = (value: string | null | undefined, errorMessage: string): string => {
  if (!value) {
    throw new Error(errorMessage)
  }

  return value
}

const mapPerWorkedDayDefinition = (
  additionalCompensation: PerWorkedDayMappableAdditionalCompensation,
  companyId: string
): ContractsPerWorkedDayDefinitionInput | null => {
  if (
    getAmountStrategyType(additionalCompensation.amountStrategyType) !== PER_WORKED_DAY_STRATEGY
  ) {
    return null
  }

  if (!additionalCompensation.perWorkedDayDefinition) {
    return null
  }

  const perWorkedDayDefinition: ContractsPerWorkedDayDefinition =
    additionalCompensation.perWorkedDayDefinition

  return {
    calculationSource: getRequiredString(
      perWorkedDayDefinition.calculationSource,
      i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.errors.calculation_source_required'
      )
    ),
    companyId,
    eligibleDays: perWorkedDayDefinition.eligibleDays,
    minimumAmountOfHoursInMinutes: perWorkedDayDefinition.minimumAmountOfHoursInMinutes,
    referenceTime: getRequiredString(
      perWorkedDayDefinition.referenceTime,
      i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.errors.reference_time_required'
      )
    ),
    timeoffLeaveTypeIds: perWorkedDayDefinition.timeoffLeaveTypeIds,
    workLocations: perWorkedDayDefinition.workLocations,
  }
}

const mapNewAdditionalCompensation = (
  additionalCompensation: InMemoryAdditionalCompensation,
  companyId: string,
  extraFields?: Partial<NewAdditionalCompensationInput>
): NewAdditionalCompensationInput => {
  const { tmpID: _, ...rest } = additionalCompensation

  return {
    amount: rest.amountStrategy?.amount,
    amountStrategyType: getAmountStrategyType(rest.amountStrategyType),
    contractVersionId: rest.contractVersionId,
    description: rest.description,
    firstPaymentOn: rest.firstPaymentOn,
    payrollConceptId: rest.payrollConceptId,
    perWorkedDayDefinition: mapPerWorkedDayDefinition(rest, companyId),
    recurrence: rest.recurrence,
    upperLimit: manageUpperLimit(rest.amountStrategy),
    ...extraFields,
  }
}

export { mapNewAdditionalCompensation }
